import { faBug, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { useEnvironment } from '../../hooks/env';
import { ClipboardButton } from '../ClipboardButton';
import { useFlagMatch } from '../FeatureFlag/FeatureFlag';
import {
  getDebugItemsSnapshot,
  subscribeToDebugItems,
} from './debugItemsStore';
import {
  isDebugEnabled,
  subscribeToDebugChanges,
  toggleDebug,
} from './debugStorage';

const GIT_COMMIT_HASH =
  typeof process !== 'undefined' && process.env.GIT_COMMIT_HASH
    ? process.env.GIT_COMMIT_HASH
    : 'dev';

export const DebugFloatingButton: FunctionComponent = () => {
  const { environment, brand } = useEnvironment();
  const flagOn = useFlagMatch({ flag: 'appDebugger', defaultValue: false });
  const enabled = useSyncExternalStore(
    subscribeToDebugChanges,
    isDebugEnabled,
    () => false,
  );
  const itemsMap = useSyncExternalStore(
    subscribeToDebugItems,
    getDebugItemsSnapshot,
    getDebugItemsSnapshot,
  );
  const items = useMemo(() => Array.from(itemsMap.values()), [itemsMap]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (environment === 'production') return;
    const handler = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.key.toLowerCase() === 'd'
      ) {
        event.preventDefault();
        toggleDebug();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [environment]);

  if (!enabled || !flagOn || environment === 'production') return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end">
      {open && (
        <div className="mb-2 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-grey-200 bg-white p-4 shadow-xl">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Debug</h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close debug panel"
              className="text-grey-500 hover:text-grey-700"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <dl className="space-y-2 text-xs">
            <div className="flex items-center justify-between gap-2">
              <dt className="text-grey-500">Commit</dt>
              <dd className="flex items-center gap-2">
                <span className="font-mono">{GIT_COMMIT_HASH}</span>
                <ClipboardButton text={GIT_COMMIT_HASH} />
              </dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-grey-500">Environment</dt>
              <dd>{environment}</dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-grey-500">Brand</dt>
              <dd>{brand}</dd>
            </div>
          </dl>
          {items.length > 0 && (
            <div className="mt-4 space-y-3 border-t border-grey-200 pt-3">
              {items.map((item) => (
                <section key={item.id}>
                  <h3 className="mb-1 text-xs font-semibold text-grey-700">
                    {item.label}
                  </h3>
                  <div className="text-xs">{item.content}</div>
                </section>
              ))}
            </div>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={open ? 'Close debug panel' : 'Open debug panel'}
        aria-expanded={open}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-grey-800 text-white shadow-lg hover:bg-grey-900 focus:outline-hidden focus:ring-2 focus:ring-grey-400"
      >
        <FontAwesomeIcon icon={faBug} />
      </button>
    </div>
  );
};
